import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import api from '../utils/api'
import { redactPersonalWhatsAppE164, redactPersonalWhatsAppUrl } from '../utils/whatsappNumbersRedact'
import { isWhatsAppHttpUrl, resolveWhatsAppHref } from '../utils/whatsappLink'

const WHATSAPP_KEYS = ['whatsappNumberSignup', 'whatsappNumberDepositWithdraw', 'whatsappNumberSupport'] as const
export type WhatsAppNumberKey = typeof WHATSAPP_KEYS[number]

export type WhatsAppNumbers = {
  whatsappNumberSignup: string
  whatsappNumberDepositWithdraw: string
  whatsappNumberSupport: string
}

const defaultNumbers: WhatsAppNumbers = {
  whatsappNumberSignup: '',
  whatsappNumberDepositWithdraw: '',
  whatsappNumberSupport: ''
}

/** Normalize to digits only; if 10 digits assume India and prepend 91 (legacy stored numbers) */
function normalizePhone(value: string): string {
  const digits = (value || '').replace(/\D/g, '')
  if (digits.length === 10) return '91' + digits
  return digits
}

type ContextValue = {
  /** Raw stored value per slot: full https URL or legacy E.164 digits */
  numbers: WhatsAppNumbers
  loading: boolean
  /** Final open-in-WhatsApp URL (admin link or legacy phone URL), optional prefill for `text` */
  getWhatsAppUrl: (key: WhatsAppNumberKey, prefillText?: string) => string
  getSupportUrl: () => string
  getSignupUrl: () => string
  getDepositWithdrawUrl: (prefillText?: string) => string
}

const WhatsAppNumbersContext = createContext<ContextValue>({
  numbers: defaultNumbers,
  loading: true,
  getWhatsAppUrl: () => 'https://api.whatsapp.com/send?phone=',
  getSupportUrl: () => 'https://api.whatsapp.com/send?phone=',
  getSignupUrl: () => 'https://api.whatsapp.com/send?phone=',
  getDepositWithdrawUrl: () => 'https://api.whatsapp.com/send?phone='
})

export function WhatsAppNumbersProvider({ children }: { children: React.ReactNode }) {
  const [numbers, setNumbers] = useState<WhatsAppNumbers>(defaultNumbers)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api.get('setting-list')
      .then((res: any) => {
        const settings = res?.data?.data?.settings || res?.data?.settings || []
        if (Array.isArray(settings)) {
          const next = { ...defaultNumbers }
          WHATSAPP_KEYS.forEach((key) => {
            const s = settings.find((x: any) => x.name === key)
            const raw = s?.value ? String(s.value).trim() : ''
            if (!raw) {
              next[key] = ''
            } else if (isWhatsAppHttpUrl(raw)) {
              next[key] = redactPersonalWhatsAppUrl(raw)
            } else {
              next[key] = redactPersonalWhatsAppE164(normalizePhone(raw))
            }
          })
          setNumbers(next)
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const getWhatsAppUrl = useCallback((key: WhatsAppNumberKey, prefillText?: string) => {
    const stored = numbers[key]
    return resolveWhatsAppHref(stored, prefillText)
  }, [numbers])

  const getSupportUrl = useCallback(() => getWhatsAppUrl('whatsappNumberSupport'), [getWhatsAppUrl])
  const getSignupUrl = useCallback(() => getWhatsAppUrl('whatsappNumberSignup'), [getWhatsAppUrl])
  const getDepositWithdrawUrl = useCallback((prefillText?: string) => getWhatsAppUrl('whatsappNumberDepositWithdraw', prefillText), [getWhatsAppUrl])

  const value: ContextValue = {
    numbers,
    loading,
    getWhatsAppUrl,
    getSupportUrl,
    getSignupUrl,
    getDepositWithdrawUrl
  }

  return (
    <WhatsAppNumbersContext.Provider value={value}>
      {children}
    </WhatsAppNumbersContext.Provider>
  )
}

export function useWhatsAppNumbers(): ContextValue {
  return useContext(WhatsAppNumbersContext)
}
